/**
 * Create an Engagement API session, server-side.
 *
 * Mirrors pype's `create-anonymous-session.ts`, which is the only description
 * of this endpoint that exists: it is REST and appears in no AsyncAPI spec,
 * so there is nothing to generate a client from. The shapes here are
 * hand-written of necessity and can drift silently if the backend adds a
 * field — a change there should be mirrored here.
 *
 * The differences from the browser version are all "there is no DOM":
 * `navigator.language`, `navigator.userAgent` and `window.location.origin`
 * have no server equivalent.
 */

/** Only the fields this runner uses. The endpoint returns more. */
export interface EngagementSession {
  accessToken: string;
  chatId: string;
  pypeId: string;
  streamId: string;
  /**
   * The consumer identity. The wire calls it `id` on the session and
   * `user_id` / `from` / `consumer` on everything after — pype reads it the
   * same way (`userId: event.data.id`). Every subsequent push needs it.
   */
  userId: string;
}

/**
 * What the customer's page tells the solution at session creation, the way
 * pype sends it (`create-anonymous-session.ts`): the page URL (a flow's
 * `GetURLParams` reads its query string), an opaque passthrough, and who the
 * customer is.
 */
export interface SessionContext {
  /** The page the widget is "on": `last_viewed_url`, and its origin as `referring_site`. */
  pageUrl?: string;
  /** Forwarded verbatim: a string, an object, or `[{ name, value }]`. */
  passthrough?: unknown;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
}

export interface CreateSessionOptions {
  appId: string;
  engagementApiUrl: string;
  language?: string;
  context?: SessionContext;
  /** So a cancelled run does not leave a request in flight. */
  signal?: AbortSignal;
}

interface AnonymousSessionResponse {
  id: string;
  access_token: string;
  chat_id: string;
  widget_data: { pype_id: string; stream_id: string };
}

/** Named so a caller can tell a bad appId from an unreachable backend. */
export class EngagementSessionError extends Error {
  readonly status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'EngagementSessionError';
    this.status = status;
  }
}

const originOf = (url: string): string | null => {
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
};

/** A test's page and customer, in pype's wire names. Empty fields are left out. */
export const contextFields = (
  context: SessionContext | undefined
): Record<string, unknown> => {
  if (!context) return {};
  const fields: Record<string, unknown> = {};
  const pageUrl = context.pageUrl?.trim();
  if (pageUrl) {
    fields.last_viewed_url = pageUrl;
    const origin = originOf(pageUrl);
    if (origin) fields.referring_site = origin;
  }
  if (context.passthrough !== undefined && context.passthrough !== '') {
    fields.passthrough = context.passthrough;
  }
  const user: [keyof SessionContext, string][] = [
    ['firstName', 'first_name'],
    ['lastName', 'last_name'],
    ['email', 'email'],
    ['phone', 'phone'],
  ];
  user.forEach(([key, wire]) => {
    const value = context[key];
    if (typeof value === 'string' && value.trim()) fields[wire] = value.trim();
  });
  return fields;
};

export const createEngagementSession = async ({
  appId,
  engagementApiUrl,
  language = 'en',
  signal,
  context,
}: CreateSessionOptions): Promise<EngagementSession> => {
  const url = `${engagementApiUrl.replace(/\/$/, '')}/messaging/v1/consumers/anonymous_session`;

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      signal,
      body: JSON.stringify({
        app_id: appId,
        browser_language: language,
        language,
        // Identifies this caller truthfully. Sending a browser-like
        // user-agent would make test runs indistinguishable from real traffic
        // in analytics, which is the opposite of what a test surface wants.
        referring_site: 'pypestream-pro-studio-testview',
        user_browser: 'pypestream-testview',
        last_name: 'unknown_user', // historical sentinel, as the widget sends
        ...contextFields(context),
      }),
    });
  } catch (err) {
    // An aborted fetch is not an unreachable backend. `fetch` rejects with the
    // same shape either way, so without this the run's own budget expiring (or
    // the caller navigating away) was reported as "Could not reach the
    // engagement API at …", which sends the reader after a network fault that
    // never happened.
    if (signal?.aborted) {
      throw new EngagementSessionError(
        'The run was stopped before the session was created.'
      );
    }

    throw new EngagementSessionError(
      `Could not reach the engagement API at ${engagementApiUrl}: ${
        err instanceof Error ? err.message : String(err)
      }`
    );
  }

  if (!response.ok) {
    throw new EngagementSessionError(
      response.status === 404
        ? `No solution is published for this channel (404). Publish it, then try again.`
        : `Session request failed (${response.status} ${response.statusText}).`,
      response.status
    );
  }

  const json = (await response.json()) as AnonymousSessionResponse;

  // `id` is checked alongside the other two because it becomes `userId`, which
  // is sent as `user_id` / `consumer` / `from` on the channel join and on every
  // `msg:send`. Undefined there is dropped by `JSON.stringify`, so the backend
  // rejects the push for a reason that names none of this. Pype's own
  // `AnonymousSessionResponse` declares `id` as required, so a response without
  // it is a broken response, not a supported shape.
  const missing = [
    !json?.access_token && 'access token',
    !json?.chat_id && 'chat id',
    !json?.id && 'user id',
  ].filter(Boolean);

  if (missing.length) {
    throw new EngagementSessionError(
      `The engagement API returned a session with no ${missing.join(', no ')}.`
    );
  }

  return {
    accessToken: json.access_token,
    chatId: json.chat_id,
    userId: json.id,
    pypeId: json.widget_data?.pype_id ?? '',
    streamId: json.widget_data?.stream_id ?? '',
  };
};
